import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import configs from "config";
import { ApiResponse, AppStatus } from "interfaces";
import { RootState } from "store";
import { get, post } from "utils";
import { wrapBaseApi } from "utils/apiWrapper";

import ls from "utils/secureStorage";

export type AuthPayload = {
  token: string | null;
  isSignedIn: boolean;
  exp: number | null;
  email: string | null;

  isVerified?: boolean;
  loginStatus?: string;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
};

type RequestCodeRo = {
  nextRequestInSecs?: number;
};

interface IState {
  verifications: string[];
  payload?: AuthPayload;

  status: "logging_in" | "signing_up" | "idle" | "error" | "success";

  checks: {
    email: { status: AppStatus; inUse: boolean };
    username: { status: AppStatus; inUse: boolean };
    phone: { status: AppStatus; inUse: boolean };
  };

  verify: {
    phone: { status: AppStatus; isSentEarlier: boolean; send?: { status: AppStatus; payload?: AuthPayload } } & RequestCodeRo;
    email: { status: AppStatus; isSentEarlier: boolean; send?: { status: AppStatus; payload?: AuthPayload } } & RequestCodeRo;
  };

  resetPassword: {
    status: AppStatus;
    isSentEarlier: boolean;
    message?: string;
  } & RequestCodeRo;

  twoFa: {
    data: {
      qrCodeLink: "";
      secret: "";
    };
    status: AppStatus;
  };
}

const initialState: IState = {
  status: "idle",
  verifications: [],

  payload: {
    isSignedIn: false,
    token: null,
    exp: null,
    email: null,
  },

  checks: {
    email: {
      status: "idle",
      inUse: false,
    },
    username: {
      status: "idle",
      inUse: false,
    },
    phone: {
      status: "idle",
      inUse: false,
    },
  },

  // 10 mins default nextRequestInSecs
  resetPassword: { status: "idle", isSentEarlier: false },

  verify: {
    phone: { status: "idle", isSentEarlier: false, send: { status: "idle" } },
    email: { status: "idle", isSentEarlier: false, send: { status: "idle" } },
  },

  twoFa: {
    status: "idle",
    data: {
      qrCodeLink: "",
      secret: "",
    },
  },
};

export const checkEmail = createAsyncThunk("auth/checkEmail", async (email: string, _) => {
  const response = await get<any>(wrapBaseApi(`/auth/check?email=${email}`));
  return response.data;
});

export const checkUsername = createAsyncThunk("auth/checkUsername", async (username: string, _) => {
  const response = await get<any>(wrapBaseApi(`/auth/check?username=${username}`));
  return response.data;
});

export const checkPhone = createAsyncThunk("auth/checkPhone", async (phone: string, _) => {
  const response = await get<any>(wrapBaseApi(`/auth/check?phone=${phone}`));
  return response.data;
});

export const generate2FA = createAsyncThunk("auth/twoFa", async (_, { rejectWithValue }) => {
  try {
    const response = await post<ApiResponse<any>, any>("/auth/2fa/generate", {});
    return response.data;
  } catch (error) {
    rejectWithValue(error);
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    hydrate: (state, action: PayloadAction<Partial<AuthPayload>>) => {
      ls.set(configs.AUTH_TOKEN_KEY, action.payload);
      state.payload = { ...action.payload } as any;
    },
    rehydrate: (state) => {
      const auth = ls.get(configs.AUTH_TOKEN_KEY) as AuthPayload;
      state.payload = auth;
    },
    dehydrate: (state) => {
      ls.remove(configs.AUTH_TOKEN_KEY);
      return { ...initialState };
    },

    reset: (state) => {
      state.status = "idle";
      state.checks = initialState.checks;
      state.payload = initialState.payload;
    },
    resetPhoneRequestTime: (state) => {
      state.verify.phone.nextRequestInSecs = undefined;
    },

    updateNextRequestTime: (
      state,
      action: PayloadAction<{ for: "resetPassword" | "verifyPhone" | "verifyEmail"; count: number }>
    ) => {
      const { for: _for, count } = action.payload;
      if (_for === "resetPassword") state.resetPassword.nextRequestInSecs = count;
      if (_for === "verifyEmail") state.verify.email.nextRequestInSecs = count;
      if (_for === "verifyPhone") state.verify.phone.nextRequestInSecs = count;
    },
  },
  extraReducers: (builder) => {
    ////////////////////
    /// Check Email
    builder.addCase(checkEmail.pending, (state, action) => {
      state.checks.email.status = "fetching";
    });
    builder.addCase(checkEmail.fulfilled, (state, { payload }) => {
      state.checks.email.status = "success";
      state.checks.email.inUse = false;
    });
    builder.addCase(checkEmail.rejected, (state, action) => {
      state.checks.email.status = "error";
      state.checks.email.inUse = true;
    });

    ////////////////////
    /// Check Username
    builder.addCase(checkUsername.pending, (state, action) => {
      state.checks.username.status = "fetching";
    });
    builder.addCase(checkUsername.fulfilled, (state, { payload }) => {
      state.checks.username.status = "success";
      state.checks.username.inUse = false;
    });
    builder.addCase(checkUsername.rejected, (state, action) => {
      state.checks.username.status = "error";
      state.checks.username.inUse = true;
    });

    ////////////////////
    /// Check Phone
    builder.addCase(checkPhone.pending, (state, action) => {
      state.checks.phone.status = "fetching";
    });
    builder.addCase(checkPhone.fulfilled, (state, { payload }) => {
      state.checks.phone.status = "success";
      state.checks.phone.inUse = false;
    });
    builder.addCase(checkPhone.rejected, (state, action) => {
      state.checks.phone.status = "error";
      state.checks.phone.inUse = true;
    });
  },
});

const { actions, reducer: AuthReducer } = authSlice;
export const selectAuth = (state: RootState) => state.auth;
export const { hydrate, dehydrate, rehydrate, reset: resetAuth, resetPhoneRequestTime, updateNextRequestTime } = actions;
export default AuthReducer;
